import { useCallback } from "react"
import type { Email } from "@stockline/shared/types/communications/email"
import { EMAIL_TABLE } from "../../../lib/offline/resources/communications/email/db"
import { useAISync } from "../../ai/providers/AISyncProvider"
import type { OptimisticRollback } from "./useSmsChatMutations"

/**
 * Email mutations are pure local optimism on top of Dexie. There is no V2
 * push pipeline for emails — the canonical write still happens against the
 * legacy FastAPI endpoint, which bumps `sync_id` and fans out `email.updated`
 * via RabbitMQ → SSE → Dexie. The chat list reads from Dexie, so without
 * an optimistic write the UI doesn't react until the SSE round-trip
 * completes (~100–500ms).
 */
export function useEmailMutations() {
  const { storage } = useAISync()

  const setArchivedOptimistic = useCallback(
    async (
      id: string,
      archived: boolean
    ): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<Email>(EMAIL_TABLE, id)
      if (!existing || existing.archived === archived) return null

      const previousArchived = existing.archived
      const previousSyncId = existing.syncId ?? 0
      await storage.put<Email>(EMAIL_TABLE, {
        ...existing,
        archived,
      })

      return async () => {
        const current = await storage.get<Email>(EMAIL_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<Email>(EMAIL_TABLE, {
          ...current,
          archived: previousArchived,
        })
      }
    },
    [storage]
  )

  const setReadOptimistic = useCallback(
    async (id: string, read: boolean): Promise<OptimisticRollback | null> => {
      const existing = await storage.get<Email>(EMAIL_TABLE, id)
      if (!existing || existing.read === read) return null

      const previousRead = existing.read
      const previousSyncId = existing.syncId ?? 0
      await storage.put<Email>(EMAIL_TABLE, {
        ...existing,
        read,
      })

      return async () => {
        const current = await storage.get<Email>(EMAIL_TABLE, id)
        if (!current) return
        if (previousSyncId < (current.syncId ?? 0)) return
        await storage.put<Email>(EMAIL_TABLE, {
          ...current,
          read: previousRead,
        })
      }
    },
    [storage]
  )

  return { setArchivedOptimistic, setReadOptimistic }
}
